import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token and tenant_id if available
api.interceptors.request.use(
  (config) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.token) {
      config.headers.Authorization = `Bearer ${user.token}`;
    }
    // Add tenant_id to request headers or body for API calls
    if (user.tenant_id) {
      config.headers['X-Tenant-ID'] = user.tenant_id;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for JWT token refresh and error handling
api.interceptors.response.use(
  (response) => {
    // Check for token refresh in response headers
    const newToken = response.headers['x-new-token'];
    const tokenExpires = response.headers['x-token-expires'];
    
    if (newToken) {
      // Update stored token
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user) {
        user.token = newToken;
        user.tokenExpires = tokenExpires;
        localStorage.setItem('user', JSON.stringify(user));
        console.log('JWT token auto-refreshed');
      }
    }
    
    return response;
  },
  (error) => {
    // Log the error for debugging
    console.error('API error:', error.response?.status, error.response?.data || error.message);

    if (error.response?.status === 401) {
      const errorCode = error.response?.data?.code;
      console.warn('Auth error code:', errorCode);

      // Only redirect on certain auth errors, not on login failures
      if (errorCode === 'INVALID_TOKEN' || errorCode === 'TOKEN_ERROR' || errorCode === 'NO_TOKEN') {
        // Unauthorized - clear user data and redirect to login
        localStorage.removeItem('user');
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  }
);

// Products API
export const productsAPI = {
  // Get all products
  list: () => api.get('/api/v1/products'),
  
  // Get product by ID
  fetch: (id) => api.get(`/api/v1/products/${id}`),
  
  // Create new product
  create: (productData) => api.post('/api/v1/products/create', productData),
  
  // Create new product with file
  createWithFile: (formData) => api.post('/api/v1/products/create', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  
  // Update product
  update: (id, productData) => api.put(`/api/v1/products/update/${id}`, productData),
  
  // Update product with file
  updateWithFile: (id, formData) => api.put(`/api/v1/products/update/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  
  // Delete product
  delete: (id) => api.delete(`/api/v1/products/delete/${id}`),
  
  // Search products
  search: (searchKey) => api.get(`/api/v1/products/search/${searchKey}`),
  
  // Get categories
  categories: () => api.get('/api/v1/products/categories'),
};

// Product Movements API
export const productMovementsAPI = {
  // Get movements by product ID
  getByProduct: (productId) => api.get(`/api/v1/product_movements/${productId}/DESC`),
  
  // Create new movement
  create: (movementData) => api.post('/api/v1/product_movements/create', movementData),
  
  // Update movement
  update: (id, movementData) => api.put(`/api/v1/product_movements/update/${id}`, movementData),
  
  // Delete movement
  delete: (id) => api.delete(`/api/v1/product_movements/delete/${id}`),
  
  // Get movement by ID
  fetch: (id) => api.get(`/api/v1/product_movements/${id}`),
};

// Users API
export const usersAPI = {
  // Get all users
  list: () => api.get('/api/v1/users'),
  
  // Get user by ID
  fetch: (id) => api.get(`/api/v1/users/${id}`),
  
  // Create new user
  create: (userData) => api.post('/api/v1/users/create', userData),
  
  // Update user (with password)
  update: (id, userData) => api.put(`/api/v1/users/update/${id}`, userData),
  
  // Update user (without password)
  updateNoPassword: (id, userData) => api.put(`/api/v1/users/update_nopassword/${id}`, userData),
  
  // Update password only
  updatePassword: (id, passwordData) => api.put(`/api/v1/users/update_password/${id}`, passwordData),
  
  // Delete user
  delete: (id) => api.delete(`/api/v1/users/delete/${id}`),
  
  // Search users
  search: (searchKey) => api.get(`/api/v1/users/search/${searchKey}`),
  
  // Login
  login: (credentials) => api.post('/api/v1/users/login', credentials),
  
  // Logout
  logout: () => api.post('/api/v1/users/logout'),
  
  // Logout all sessions
  logoutAll: () => api.post('/api/v1/users/logout-all'),
  
  // Get active sessions
  getActiveSessions: () => api.get('/api/v1/users/sessions'),
  
  // Revoke a specific session
  revokeSession: (sessionId) => api.delete(`/api/v1/users/sessions/${sessionId}`),
};

// Dashboard API
export const dashboardAPI = {
  // Today's sales
  posSalesToday: () => api.get('/api/v1/dashboard/pos_sales_today'),
  
  // Sales by category today
  posSalesByCategoryToday: () => api.get('/api/v1/dashboard/pos_sale_today_by_category'),
  
  // Sales by payment method today
  posSalesByPaymentMethodToday: () => api.get('/api/v1/dashboard/pos_sale_today_by_payment_method'),
  
  // Top products today
  posTopProductsToday: () => api.get('/api/v1/dashboard/pos_sale_today_top_products'),
  
  // Customers served today
  posCustomersServed: () => api.get('/api/v1/dashboard/pos_customers_served'),
  
  // Sales by staff today
  posSalesByStaffToday: () => api.get('/api/v1/dashboard/pos_sale_today_by_staff'),
  
  // Stock warnings
  posProductsStockWarning: () => api.get('/api/v1/dashboard/pos_products_today_stock_warning'),
  
  // Recent transactions
  recentTransactions: () => api.get('/api/v1/dashboard/recent_transactions'),
  
  // Recent product movements
  recentProductMovements: () => api.get('/api/v1/dashboard/recent_product_movements'),
  
  // Fast moving products
  fastMovingProducts: (from, to) => api.get(`/api/v1/dashboard/fast_moving_products/${from}/${to}`),
  
  // Business figures with comparison
  businessFigures: (from, to) => api.get(`/api/v1/dashboard/business_figures/${from}/${to}`),
  
  // Sales trend
  salesTrend: (from, to) => api.get(`/api/v1/dashboard/sales_trend/${from}/${to}`),
};

// Transactions API
export const transactionsAPI = {
  // Get all transactions
  list: (page = 1) => api.get(`/api/v1/transactions/${page}`),
  
  // Search transactions
  search: (searchKey, page = 1) => api.get(`/api/v1/transactions/search/${searchKey}/${page}`),
  
  // Get transaction by ID
  fetch: (id) => api.get(`/api/v1/transactions/fetch/${id}`),
  
  // Get transaction details
  fetchDetails: (transactionId) => api.get(`/api/v1/transactions/details/${transactionId}`),
  
  // Create new transaction
  create: (transactionData) => api.post('/api/v1/transactions/create', transactionData),
  
  // Create batch transactions
  createBatch: (transactionsData) => api.post('/api/v1/transactions/create_batch', transactionsData),
  
  // Get transactions by product
  withProductId: (productId) => api.get(`/api/v1/transactions/with_product_id/${productId}`),
  
  // Get transactions by customer
  withCustomerId: (customerId) => api.get(`/api/v1/transactions/with_customer_id/${customerId}`),
  
  // Update transaction status
  updateStatus: (id, statusData) => api.put(`/api/v1/transactions/updateStatus/${id}`, statusData),
};

// Customers API
export const customersAPI = {
  // Get all customers
  list: () => api.get('/api/v1/customers'),
  
  // Search customers
  search: (searchKey) => api.get(`/api/v1/customers/search/${searchKey}`),
  
  // Get customer by ID
  fetch: (id) => api.get(`/api/v1/customers/${id}`),
  
  // Create new customer
  create: (customerData) => api.post('/api/v1/customers/create', customerData),
  
  // Update customer
  update: (id, customerData) => api.put(`/api/v1/customers/update/${id}`, customerData),
  
  // Delete customer
  delete: (id) => api.delete(`/api/v1/customers/delete/${id}`),

  // Get customer summary (sales data)
  getSummary: (id) => api.get(`/api/v1/customers/${id}/summary`),

  // Get all customers with totals
  getAll: () => api.get('/api/v1/customers'),
};

// General API utilities
export const apiUtils = {
  // Handle API errors
  handleError: (error) => {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.message) {
      return error.message;
    }
    return 'An unexpected error occurred';
  },
  
  // Format API response data
  formatResponse: (response) => {
    // console.log('Response:', response);
    // console.log('API Response:', response.data.data);
    return response.data.data;
  },

  // Upload image
  upload: (imageData) => api.post('/api/v1/img/upload', imageData),

  // Centralized logout utility
  logout: () => {
    localStorage.removeItem('user');
    window.location.href = '/';
  },
};

// Settings API
export const settingsAPI = {
  // Get tenant settings
  fetch: () => api.get('/api/v1/settings'),
  
  // Update tenant settings
  update: (settingsData) => api.put('/api/v1/settings', settingsData),
  
  // Create/Update tenant settings (same as update)
  save: (settingsData) => api.post('/api/v1/settings', settingsData),
};

// POS API for service charge calculations
export const posAPI = {
  // Calculate service charge for a given subtotal
  calculateServiceCharge: (subtotal) => api.post('/api/v1/pos/calculate-service-charge', { subtotal }),
  
  // Get current settings for POS calculations
  getSettings: () => api.get('/api/v1/settings')
};

// Held Transactions API
export const heldTransactionsAPI = {
  // Get all held transactions for tenant
  list: () => api.get('/api/v1/held-transactions'),
  
  // Create a new held transaction
  create: (transactionData) => api.post('/api/v1/held-transactions/create', transactionData),
  
  // Get a specific held transaction by reference number
  fetchByReference: (reference) => api.get(`/api/v1/held-transactions/reference/${reference}`),
  
  // Update a held transaction by reference number
  updateByReference: (reference, transactionData) => api.put(`/api/v1/held-transactions/reference/update/${reference}`, transactionData),
  
  // Delete a held transaction by reference number
  deleteByReference: (reference) => api.delete(`/api/v1/held-transactions/reference/delete/${reference}`),
  
  // Backward compatibility methods (using ID)
  // Get a specific held transaction by ID
  fetch: (id) => api.get(`/api/v1/held-transactions/${id}`),
  
  // Update a held transaction by ID
  update: (id, transactionData) => api.put(`/api/v1/held-transactions/update/${id}`, transactionData),
  
  // Delete a held transaction by ID
  delete: (id) => api.delete(`/api/v1/held-transactions/delete/${id}`)
};

export default api;
